import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import LandingScreen from '@/components/clusterscope/LandingScreen';
import AnalysisModeScreen from '@/components/clusterscope/AnalysisModeScreen';
import LoadingScreen from '@/components/clusterscope/LoadingScreen';
import ClusterDetectionScreen from '@/components/clusterscope/ClusterDetectionScreen';
import WalletIntelligenceScreen from '@/components/clusterscope/WalletIntelligenceScreen';
import TokenScannerScreen from '@/components/clusterscope/TokenScannerScreen';
import { getDataset, type ScanContext, ANSEM_DATASET } from '@/data/clusterscope-data';

type Screen = 'landing' | 'analysis' | 'token-scanner' | 'loading' | 'clusters' | 'wallet-intel';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

export const HomePage: React.FC = () => {
  const [screen, setScreen] = useState<Screen>('landing');
  const [scanContext, setScanContext] = useState<ScanContext>({ mode: 'token', label: 'ANSEM' });
  const [dataset, setDataset] = useState<typeof ANSEM_DATASET>(ANSEM_DATASET);
  const [walletAddress, setWalletAddress] = useState('');
  const [tokenSearchValue, setTokenSearchValue] = useState('');

  const handleScanToken = async (label: string) => {
    const ds = await getDataset(label);
    setDataset(ds);
    setScanContext({ mode: 'token', label: ds.token.symbol || label });
    setScreen('loading');
  };

  const handleScanWallet = (address: string) => {
    setWalletAddress(address);
    setScanContext({ mode: 'wallet', label: address });
    setScreen('loading');
  };

  const handleTokenSearchSubmit = () => {
    if (tokenSearchValue.trim()) {
      handleScanToken(tokenSearchValue.trim());
    }
  };

  const handleSwitchMode = (mode: 'token' | 'wallet') => {
    setDataset(ANSEM_DATASET);
    setWalletAddress('');
    if (mode === 'token') {
      setScanContext({ mode: 'token', label: 'ANSEM' });
      setScreen('token-scanner');
    } else {
      setScanContext({ mode: 'wallet', label: '' });
      setScreen('wallet-intel');
    }
  };

  const handleLoadingComplete = () => {
    if (scanContext.mode === 'token') {
      setScreen('clusters');
    } else {
      setScreen('wallet-intel');
    }
  };

  const handleRescanToken = async (label: string) => {
    const ds = await getDataset(label);
    setDataset(ds);
    setScanContext({ mode: 'token', label: ds.token.symbol || label });
  };

  const handleRescanWallet = (address: string) => {
    setWalletAddress(address);
  };

  const handleNewSearch = () => {
    setDataset(ANSEM_DATASET);
    setWalletAddress('');
    setScanContext({ mode: 'token', label: 'ANSEM' });
    setScreen('analysis');
  };

  return (
    <AnimatePresence mode="wait">
      {screen === 'landing' && (
        <motion.div
          key="landing"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: easeOut }}
        >
          <LandingScreen
            dataset={dataset}
            onBeginAnalysis={() => setScreen('analysis')}
            onScanWallet={handleScanWallet}
          />
        </motion.div>
      )}

      {screen === 'analysis' && (
        <motion.div
          key="analysis"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: easeOut }}
        >
          <AnalysisModeScreen
            dataset={dataset}
            onBack={() => setScreen('landing')}
            onScanToken={handleScanToken}
            onScanWallet={handleScanWallet}
          />
        </motion.div>
      )}

      {screen === 'token-scanner' && (
        <motion.div
          key="token-scanner"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: easeOut }}
        >
          <TokenScannerScreen
            dataset={dataset}
            searchValue={tokenSearchValue}
            onSearchChange={setTokenSearchValue}
            onSearchSubmit={handleTokenSearchSubmit}
            onNewSearch={handleNewSearch}
            onSwitchMode={handleSwitchMode}
            onScanToken={handleScanToken}
          />
        </motion.div>
      )}

      {screen === 'loading' && (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: easeOut }}
        >
          <LoadingScreen scanContext={scanContext} onComplete={handleLoadingComplete} />
        </motion.div>
      )}

      {screen === 'clusters' && (
        <motion.div
          key="clusters"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: easeOut }}
        >
          <ClusterDetectionScreen
            dataset={dataset}
            scanContext={scanContext}
            onNewSearch={handleNewSearch}
            onSwitchMode={handleSwitchMode}
            onRescanToken={handleRescanToken}
          />
        </motion.div>
      )}

      {screen === 'wallet-intel' && (
        <motion.div
          key="wallet-intel"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: easeOut }}
        >
          <WalletIntelligenceScreen
            dataset={dataset}
            initialAddress={walletAddress}
            onSwitchMode={handleSwitchMode}
            onWalletScanned={handleRescanWallet}
            onNewSearch={handleNewSearch}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HomePage;